import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Wind, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import useSeo from "@/hooks/useSeo";

export default function AdminLogin() {
  useSeo({ title: "Admin Login | Full Indoor Air Services" });
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@fullindoorair.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.role) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-12 bg-bone" data-testid="admin-login-page">
      <div className="w-full max-w-md card-surface p-10">
        <div className="flex items-center gap-2 mb-8">
          <span className="inline-flex w-10 h-10 rounded-full bg-forest text-white items-center justify-center">
            <Wind size={18} />
          </span>
          <div>
            <div className="font-heading text-lg">Full Indoor Air</div>
            <div className="eyebrow">Admin portal</div>
          </div>
        </div>
        <h1 className="font-heading text-3xl tracking-tight mb-2">Welcome back.</h1>
        <p className="text-sm text-muted2 mb-8">Sign in to manage bookings and clients.</p>

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <input
            className="input-base"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="login-email"
          />
          <input
            className="input-base"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            data-testid="login-password"
          />
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg" data-testid="login-error">
              {error}
            </div>
          )}
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="login-submit">
            <Lock size={14} /> {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
